import { createContext, useContext, useEffect, useState } from "react";
import { BrowserRouter, NavLink, Navigate, Route, Routes } from "react-router-dom";
import { FileSignature, LogOut, Menu, Shield } from "lucide-react";
import { api } from "./lib/api";
import { completeSignIn, idToken, loadConfig, signIn, signInReady, signOut } from "./lib/auth";
import type { Me } from "./lib/types";
import { Button, cx, ErrorNote, Spinner, ToastHost } from "./components/ui";
import { Logo, MicrosoftMark } from "./components/icons";
import Dashboard from "./pages/Dashboard";
import EnvelopePage from "./pages/EnvelopePage";
import Admin from "./pages/Admin";
import { SelfSign, SignerApp } from "./pages/Sign";

// Runs once even under StrictMode's double effect: the sign-in code can only be exchanged once.
let boot: Promise<{ err: string | null; signedIn: boolean }> | null = null;
const MeContext = createContext<Me | null>(null);
export const useMe = () => useContext(MeContext)!;

export default function App() {
  // Signers have no account: their pages never touch Cognito.
  const signer = /^\/s\/([A-Za-z0-9_-]{43})\/?$/.exec(location.pathname);
  if (signer) return <SignerApp token={signer[1]} />;
  return <SenderApp />;
}

function SenderApp() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "signin" | "ready">("loading");

  useEffect(() => {
    boot ??= (async () => {
      await loadConfig();
      const err = await completeSignIn();
      return { err, signedIn: Boolean(await idToken()) };
    })();
    boot
      .then(async ({ err, signedIn }) => {
        if (err) setError(err);
        if (!signedIn) return setState("signin");
        setMe(await api<Me>("/me"));
        setState("ready");
      })
      .catch((e) => {
        setError((e as Error).message);
        setState("signin");
      });
  }, []);

  if (state === "loading") return <Spinner />;
  if (state === "signin" || !me) return <SignIn error={error} />;
  return (
    <MeContext.Provider value={me}>
      <BrowserRouter>
        <Routes>
          <Route path="/envelopes/:id/sign" element={<SelfSign />} />
          <Route
            path="*"
            element={
              <Shell>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/envelopes/:id" element={<EnvelopePage />} />
                  {me.isAdmin && <Route path="/admin" element={<Admin />} />}
                  {me.isAdmin && <Route path="/admin/envelopes/:id" element={<EnvelopePage admin />} />}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Shell>
            }
          />
        </Routes>
        <ToastHost />
      </BrowserRouter>
    </MeContext.Provider>
  );
}

function SignIn({ error }: { error: string | null }) {
  const ready = signInReady();
  return (
    <main className="min-h-full flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-surface border border-line rounded-2xl p-8 shadow-sm text-center">
        <Logo className="size-12 mx-auto" />
        <h1 className="text-2xl font-semibold mt-4">SignIt</h1>
        <p className="text-sm text-muted mt-1">Send documents for e-signature</p>
        {error && (
          <div className="mt-6 text-left">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}
        <Button variant="primary" className="w-full mt-6 h-11" disabled={!ready} onClick={() => signIn(location.pathname + location.search)}>
          <MicrosoftMark className="size-4" /> Sign in with Microsoft
        </Button>
        {!ready && <p className="text-xs text-muted mt-3">Microsoft sign-in isn't set up yet.</p>}
        <p className="text-xs text-muted mt-6">For Intellias staff. Signing a document someone sent you? Use the link in your email; you don't need an account.</p>
      </div>
    </main>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const link = ({ isActive }: { isActive: boolean }) => cx("flex items-center gap-3 rounded-lg px-3 h-10 text-sm font-medium", isActive ? "bg-accent-chip text-accent-text" : "text-ink-2 hover:bg-surface-2");
  const nav = (
    <nav className="flex flex-col gap-1" onClick={() => setOpen(false)}>
      <NavLink to="/" end className={link}>
        <FileSignature className="size-4" /> Envelopes
      </NavLink>
      {me.isAdmin && (
        <NavLink to="/admin" className={link}>
          <Shield className="size-4" /> Admin
        </NavLink>
      )}
    </nav>
  );
  const account = (
    <div className="px-3 flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{me.name}</div>
        <div className="text-xs text-muted truncate">{me.email}</div>
      </div>
      <button onClick={() => signOut()} className="p-2 rounded-lg text-muted hover:bg-surface-2" aria-label="Sign out" title="Sign out">
        <LogOut className="size-4" />
      </button>
    </div>
  );
  return (
    <div className="flex h-full">
      <aside className="hidden md:flex w-60 shrink-0 flex-col gap-6 border-r border-line bg-surface p-4">
        <Brand />
        {nav}
        <div className="mt-auto">{account}</div>
      </aside>
      {open && (
        <div className="md:hidden fixed inset-0 z-40 bg-scrim" onClick={() => setOpen(false)}>
          <aside className="w-72 h-full bg-surface p-4 flex flex-col gap-6" onClick={(e) => e.stopPropagation()}>
            <Brand />
            {nav}
            <div className="mt-auto">{account}</div>
          </aside>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="md:hidden flex items-center gap-2 border-b border-line bg-surface px-3 h-14">
          <button onClick={() => setOpen(true)} className="p-2 rounded-lg hover:bg-surface-2" aria-label="Menu">
            <Menu className="size-5" />
          </button>
          <Brand />
        </header>
        <main className="flex-1 min-h-0 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}

export function Brand() {
  return (
    <div className="flex items-center gap-2 px-1">
      <Logo className="size-7" />
      <span className="font-semibold text-ink">SignIt</span>
    </div>
  );
}
